// src/components/reader/SectionDrills.jsx
//
// Per-section "Drill It" (teach -> see -> revise -> test -> hear): the quizzes and
// flashcards linked to THIS section, rendered between the section body and its audio.
// Extracted from PackReaderGrouped (settext-section-drills) so the set-text reader --
// where Danielle's linked quizzes were silently never shown -- renders the identical
// block from the identical code.
//
// Renders NOTHING when the section has no runnable linked quiz and no linked flashcard
// (sectionDrillsFor.visible). `.selectable` lifts the analysis-content copy-protection
// for the interactive runner; the per-section keys remount both runners per section so
// answers / resume state never leak across sections. Quiz progress persists under a
// per-section drill key through the same useProgress API as the pooled quiz (rules
// already allow arbitrary quizScores / quizState keys).
import React, { useMemo } from 'react';
import PracticeQuiz from '../practice/PracticeQuiz.jsx';
import PracticeFlashcards from '../practice/PracticeFlashcards.jsx';
import { sectionDrillsFor } from './sectionDrills.js';

export const drillKey = (sectionId) => 'drill:' + sectionId;

export default function SectionDrills({
    section, quizItems, flashcardItems, knownItemIds, showLockedCta,
    recordQuizRun, persistEnabled, quizState, saveQuizState,
}) {
    const sectionId = section ? section.id : null;
    const drills = useMemo(
        () => sectionDrillsFor(quizItems, flashcardItems, knownItemIds, sectionId),
        [quizItems, flashcardItems, knownItemIds, sectionId],
    );
    if (!drills.visible) return null;
    const key = drillKey(sectionId);
    return (
        <div className="selectable mt-10">
            <div className="flex items-baseline gap-3 mb-4 pb-2 border-b border-book-accent/30 text-book-accent">
                <h3 className="font-display text-sm font-bold text-book-muted">Drill It</h3>
                <span className="font-serif italic text-book-muted text-xs">Quick practice for this section</span>
            </div>
            {/* Flash cards first (revise), then quiz (test). Both components render their
                own headers, so no extra sub-labels; a quiz-only section renders exactly as
                before. The cards get the SECTION title (not the pack title). No progress
                persistence for cards -- matches the pooled deck. */}
            {drills.flashcards.length > 0 && (
                <div className={drills.runnable ? 'mb-8' : ''}>
                    <PracticeFlashcards key={'drillfc:' + sectionId}
                        deck={drills.flashcards}
                        showCta={showLockedCta}
                        title={(section.content && section.content.title) || ''} />
                </div>
            )}
            {drills.runnable && (
                <PracticeQuiz key={key}
                    embedded
                    items={drills.quizzes}
                    showLockedCta={showLockedCta}
                    onComplete={(r) => recordQuizRun(key, r)}
                    persistEnabled={persistEnabled}
                    savedQuizState={quizState ? quizState[key] : undefined}
                    onSaveProgress={(blob) => saveQuizState(key, blob)} />
            )}
        </div>
    );
}
